/**
 * Custom hook for fetching and managing Scheduler V1 data
 * Handles legs data, running totals, filter options, and selection state
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useVisibilityAwareInterval } from './useVisibilityAwareInterval';
import schedulingService from '../services/scheduling.service';
import { isSchedulerLegSelectable } from '../utils/prepullRecovery';
import {
  SchedulingLeg,
  SchedulerFilters,
  LegScheduleUpdate,
  RunningTotalsResponse,
  FilterOptionsResponse,
} from '../types/scheduling';
import { useRealtimeCollaboration, LiveUpdate, ActiveUser } from './useRealtimeCollaboration';
import { Socket } from 'socket.io-client';

// Field editing types for real-time collaboration
export interface FieldEditor {
  user_id: string;
  user_name: string;
  field: string;  // 'prepull_date' | 'appt_date' | 'appt_time_start' | 'appt_time_end'
  pronumber: number;
  value?: string;  // Current value they're typing/selecting
  timestamp: number;
}

export type FieldEditorsMap = Map<string, FieldEditor>;  // key: `${pronumber}-${field}`

export interface SchedulerDataState {
  legs: SchedulingLeg[];
  runningTotals: RunningTotalsResponse | null;
  filterOptions: FilterOptionsResponse | null;
  selectedLegs: Set<string>;
  loading: boolean;
  loadingTotals: boolean;
  loadingOptions: boolean;
  error: string | null;
}

interface UseSchedulerDataOptions {
  /** Unique page identifier for socket.io room scoping (e.g., 'scheduler', 'lc') */
  pageId: string;
  userName?: string;
  userId?: string;
  userEmail?: string;
  enableRealtime?: boolean;
  initialFilters?: Partial<SchedulerFilters>;
  /** Which statuses are selectable for bulk actions (default: PLANNING, RETURNED) */
  selectableStatuses?: string[];
  /** Statuses to exclude when 'ALL' is selected (e.g., HOLD, PENDING_CUSTOMER for Scheduler) */
  excludeStatusesOnAll?: string[];
  /** Ref that, when true, defers silent refetches until it becomes false (e.g., during drag-to-fill) */
  isDeferringRefetch?: React.RefObject<boolean>;
  /** Orders-scope registry fieldnames the page has added as columns. Part of the
   *  request, so a change here has to refetch — the two scheduler pages keep
   *  separate selections and share this hook. */
  additionalFields?: string[];
}

interface UseSchedulerDataReturn extends SchedulerDataState {
  newPronumbers: Set<number>;
  clearNewPronumbers: () => void;
  filters: SchedulerFilters;
  setFilters: (filters: SchedulerFilters) => void;
  fetchLegs: () => Promise<void>;
  fetchRunningTotals: () => Promise<void>;
  fetchFilterOptions: () => Promise<void>;
  updateLegSchedule: (
    pronumber: number,
    update: LegScheduleUpdate
  ) => Promise<void>;
  sendToLC: (pronumbers: Array<{ pronumber: number }>) => Promise<number>;
  finalizeLegs: (pronumbers: Array<{ pronumber: number }>) => Promise<{ count: number; errors?: string[] }>;
  finalizePrepull: (pronumbers: Array<{ pronumber: number }>) => Promise<{ count: number; errors?: string[] }>;
  returnToScheduler: (
    pronumbers: Array<{ pronumber: number }>,
    targetDate?: string,
    targetOnOrBy?: 'ON' | 'BY'
  ) => Promise<{ count: number; errors?: string[] }>;
  toggleLegSelection: (pronumber: number) => void;
  selectAllLegs: () => void;
  selectLegs: (keys: string[]) => void;
  deselectLegs: (keys: string[]) => void;
  clearSelection: () => void;
  refetch: () => Promise<void>;
  activeUsers: Array<{ sid: string; user_name: string; user_id: string }>;
  isConnected: boolean;
  socket: Socket | null;
  // Field editing collaboration
  fieldEditors: FieldEditorsMap;
  broadcastFieldFocus: (pronumber: number, field: string) => void;
  broadcastFieldBlur: (pronumber: number, field: string) => void;
  broadcastFieldValue: (pronumber: number, field: string, value: string) => void;
  getFieldEditor: (pronumber: number, field: string) => FieldEditor | null;
}

const DEFAULT_FILTERS: SchedulerFilters = {
  move_type: 'ALL',
  status: ['ALL'],
};

export const useSchedulerData = (
  options: UseSchedulerDataOptions
): UseSchedulerDataReturn => {
  const { pageId, userName, userId, userEmail, enableRealtime = true, initialFilters, selectableStatuses = ['PLANNING', 'RETURNED'], excludeStatusesOnAll, isDeferringRefetch, additionalFields } = options;

  // Read through a ref inside fetchLegsInternal, which is re-created every render
  // and captured by socket handlers and timers — a stale closure there would keep
  // requesting the previous selection. The effect below is what triggers the
  // refetch when the selection actually changes.
  const additionalFieldsRef = useRef<string[] | undefined>(additionalFields);
  additionalFieldsRef.current = additionalFields;
  const additionalFieldsKey = (additionalFields ?? []).join(',');

  const [filters, setFiltersState] = useState<SchedulerFilters>({
    ...DEFAULT_FILTERS,
    ...initialFilters,
  });
  const [legs, setLegs] = useState<SchedulingLeg[]>([]);
  const [runningTotals, setRunningTotals] = useState<RunningTotalsResponse | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptionsResponse | null>(null);
  const [selectedLegs, setSelectedLegs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingTotals, setLoadingTotals] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New pronumber detection (highlight new rows)
  const [newPronumbers, setNewPronumbers] = useState<Set<number>>(new Set());
  const knownPronumbersRef = useRef<Set<number>>(new Set());
  const isPageVisibleRef = useRef(true);
  const initialLoadDoneRef = useRef(false);
  const visibleHighlightTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearNewPronumbers = useCallback(() => {
    setNewPronumbers(new Set());
    if (visibleHighlightTimerRef.current) {
      clearTimeout(visibleHighlightTimerRef.current);
      visibleHighlightTimerRef.current = null;
    }
  }, []);

  // Field editing collaboration state
  const [fieldEditors, setFieldEditors] = useState<FieldEditorsMap>(new Map());
  const fieldEditorsRef = useRef<FieldEditorsMap>(new Map());
  const fieldHeartbeatRef = useRef<NodeJS.Timeout | null>(null);
  const currentFieldRef = useRef<{ pronumber: number; field: string } | null>(null);

  // Keep ref in sync with state for stable getFieldEditor
  fieldEditorsRef.current = fieldEditors;

  // Debounce ref for filter changes
  const filterTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle real-time updates
  const handleLiveUpdate = useCallback((update: LiveUpdate) => {
    // Ignore updates from the current user (we already have optimistic updates)
    if (update.user?.user_id === userId) {
      return;
    }

    if (update.update_type === 'leg_updated') {
      // Update the specific leg in local state (no full refetch)
      const { pronumber, update: legUpdate } = update.payload;
      if (pronumber && legUpdate) {
        setLegs(prev => prev.map(leg => {
          if (leg.pronumber !== pronumber) return leg;
          const updatedLeg = { ...leg, ...legUpdate };

          // Auto-manage status optimistically (same logic as local updates)
          if (!('schedule_status' in legUpdate)) {
            const isValidDate = (d: string | null | undefined): boolean => {
              if (!d) return false;
              const parsed = new Date(d);
              return !isNaN(parsed.getTime()) && parsed.getFullYear() > 1970;
            };
            const hasDates = isValidDate(updatedLeg.proposed_appt_date) || isValidDate(updatedLeg.proposed_prepull_date);
            if (hasDates && !leg.schedule_status) {
              updatedLeg.schedule_status = 'PLANNING';
            } else if (!hasDates && leg.schedule_status === 'PLANNING') {
              updatedLeg.schedule_status = null;
            }
          }

          return updatedLeg;
        }));
      }
    } else if (['sent_to_lc', 'legs_finalized', 'prepull_finalized', 'legs_returned', 'set_to_hold', 'set_to_pending_customer'].includes(update.update_type)) {
      // Refresh both legs and totals when bulk status changes occur (silent to preserve filters)
      fetchLegsInternal(filters, true);
      fetchRunningTotalsInternal(filters.terminal);
    } else if (update.update_type === 'field_focus') {
      // Another user started editing a field
      const { pronumber, field, value } = update.payload;
      const key = `${pronumber}-${field}`;
      setFieldEditors(prev => {
        const next = new Map(prev);
        next.set(key, {
          user_id: update.user.user_id,
          user_name: update.user.user_name,
          pronumber,
          field,
          value,
          timestamp: Date.now(),
        });
        return next;
      });
    } else if (update.update_type === 'field_blur') {
      // Another user stopped editing a field
      const { pronumber, field } = update.payload;
      const key = `${pronumber}-${field}`;
      setFieldEditors(prev => {
        const next = new Map(prev);
        // Only remove if it's from the same user
        const existing = next.get(key);
        if (existing && existing.user_id === update.user.user_id) {
          next.delete(key);
        }
        return next;
      });
    } else if (update.update_type === 'field_value') {
      // Another user changed the value in a field they're editing
      const { pronumber, field, value } = update.payload;
      const key = `${pronumber}-${field}`;
      setFieldEditors(prev => {
        const existing = prev.get(key);
        if (existing && existing.user_id === update.user.user_id) {
          const next = new Map(prev);
          next.set(key, { ...existing, value, timestamp: Date.now() });
          return next;
        }
        return prev;
      });
    } else if (update.update_type === 'field_heartbeat') {
      // Heartbeat to keep field editor alive
      const { pronumber, field, value } = update.payload;
      const key = `${pronumber}-${field}`;
      setFieldEditors(prev => {
        const existing = prev.get(key);
        if (existing && existing.user_id === update.user.user_id) {
          const next = new Map(prev);
          next.set(key, { ...existing, value, timestamp: Date.now() });
          return next;
        }
        return prev;
      });
    } else if (update.update_type === 'request_field_state') {
      // New user joined - broadcast our current field editing state
      if (currentFieldRef.current) {
        broadcastUpdate('field_focus', {
          pronumber: currentFieldRef.current.pronumber,
          field: currentFieldRef.current.field,
        });
      }
    }
  }, [filters, userId]);

  // Handle user joined - request their field state
  const handleUserJoined = useCallback(() => {
    // When a new user joins, broadcast our current editing state so they see it
    if (currentFieldRef.current) {
      // Small delay to ensure the new user is ready to receive
      setTimeout(() => {
        broadcastUpdateRef.current?.('field_focus', {
          pronumber: currentFieldRef.current?.pronumber,
          field: currentFieldRef.current?.field,
        });
      }, 500);
    }
  }, []);

  // Handle user left - clean up their field editors
  const handleUserLeft = useCallback((data: { user: ActiveUser }) => {
    const leftUserId = data.user.user_id;
    setFieldEditors(prev => {
      const next = new Map(prev);
      for (const [key, editor] of next.entries()) {
        if (editor.user_id === leftUserId) {
          next.delete(key);
        }
      }
      return next;
    });
  }, []);

  // Ref to hold broadcastUpdate for use in callbacks
  const broadcastUpdateRef = useRef<((type: string, payload: any) => void) | null>(null);

  // Real-time collaboration
  const { activeUsers, broadcastUpdate, isConnected, socket } = useRealtimeCollaboration({
    pageId,
    userName,
    userId,
    userEmail,
    onLiveUpdate: handleLiveUpdate,
    onUserJoined: handleUserJoined,
    onUserLeft: handleUserLeft,
    enabled: enableRealtime,
  });

  // Keep broadcastUpdate ref up to date
  useEffect(() => {
    broadcastUpdateRef.current = broadcastUpdate;
  }, [broadcastUpdate]);

  // Clean up stale field editors (no heartbeat in 10 seconds)
  useEffect(() => {
    const cleanup = setInterval(() => {
      const now = Date.now();
      setFieldEditors(prev => {
        let hasChanges = false;
        const next = new Map(prev);
        for (const [key, editor] of next.entries()) {
          if (now - editor.timestamp > 10000) {
            next.delete(key);
            hasChanges = true;
          }
        }
        return hasChanges ? next : prev;
      });
    }, 5000);
    return () => clearInterval(cleanup);
  }, []);

  // Field editing functions
  const broadcastFieldFocus = useCallback((pronumber: number, field: string) => {
    currentFieldRef.current = { pronumber, field };
    broadcastUpdate('field_focus', { pronumber, field });

    // Start heartbeat
    if (fieldHeartbeatRef.current) {
      clearInterval(fieldHeartbeatRef.current);
    }
    fieldHeartbeatRef.current = setInterval(() => {
      if (currentFieldRef.current) {
        broadcastUpdate('field_heartbeat', {
          pronumber: currentFieldRef.current.pronumber,
          field: currentFieldRef.current.field,
        });
      }
    }, 3000);
  }, [broadcastUpdate]);

  const broadcastFieldBlur = useCallback((pronumber: number, field: string) => {
    currentFieldRef.current = null;
    broadcastUpdate('field_blur', { pronumber, field });

    // Stop heartbeat
    if (fieldHeartbeatRef.current) {
      clearInterval(fieldHeartbeatRef.current);
      fieldHeartbeatRef.current = null;
    }
  }, [broadcastUpdate]);

  const broadcastFieldValue = useCallback((pronumber: number, field: string, value: string) => {
    broadcastUpdate('field_value', { pronumber, field, value });
  }, [broadcastUpdate]);

  // Stable getFieldEditor - uses ref so it doesn't change when fieldEditors updates
  // This prevents column recreation on every field editor change
  const getFieldEditor = useCallback((pronumber: number, field: string): FieldEditor | null => {
    const key = `${pronumber}-${field}`;
    return fieldEditorsRef.current.get(key) || null;
  }, []);

  // Cleanup heartbeat on unmount
  useEffect(() => {
    return () => {
      if (fieldHeartbeatRef.current) {
        clearInterval(fieldHeartbeatRef.current);
      }
    };
  }, []);

  // All possible statuses (excluding 'ALL' which is a filter option, not a real status)
  const ALL_STATUSES = ['PLANNING', 'PENDING', 'PENDING_APPT', 'RETURNED', 'RESCHEDULED', 'HOT_ORDER', 'FINALIZED', 'HOLD', 'PENDING_CUSTOMER'];

  // Track whether a deferred refetch is pending
  const deferredRefetchRef = useRef(false);

  // Track pending optimistic edits so polling doesn't overwrite them.
  // Each entry maps a pronumber to the fields the user just changed + a timestamp.
  // Entries expire after PENDING_EDIT_TTL_MS so stale overrides don't linger.
  const PENDING_EDIT_TTL_MS = 5000;
  const pendingEditsRef = useRef<Map<number, { updates: Partial<SchedulingLeg>; timestamp: number }>>(new Map());

  // Internal fetch functions (no filter dependency)
  // silent=true skips loading state so background refreshes don't reset the table
  const fetchLegsInternal = async (currentFilters: SchedulerFilters, silent = false) => {
    // Defer silent refetches while a drag operation is in progress to prevent
    // row data from shifting under the user mid-drag
    if (silent && isDeferringRefetch?.current) {
      deferredRefetchRef.current = true;
      return;
    }

    try {
      if (!silent) setLoading(true);
      setError(null);

      // If 'ALL' is selected and we have statuses to exclude, replace 'ALL' with explicit statuses
      let filtersToSend = currentFilters;
      if (excludeStatusesOnAll && excludeStatusesOnAll.length > 0 && currentFilters.status?.includes('ALL')) {
        const allowedStatuses = ALL_STATUSES.filter(s => !excludeStatusesOnAll.includes(s));
        filtersToSend = {
          ...currentFilters,
          status: allowedStatuses as any,
        };
      }

      const data = await schedulingService.getLegs({
        ...filtersToSend, include_prepull_needed: pageId === 'scheduler',
      }, additionalFieldsRef.current);

      // Detect new pronumbers on background refresh
      const currentPros = new Set(data.map(leg => leg.pronumber));
      if (initialLoadDoneRef.current) {
        const newPros: number[] = [];
        for (const pro of currentPros) {
          if (!knownPronumbersRef.current.has(pro)) {
            newPros.push(pro);
          }
        }
        if (newPros.length > 0) {
          setNewPronumbers(prev => {
            const next = new Set(prev);
            newPros.forEach(p => next.add(p));
            return next;
          });
          // If page is visible, auto-clear after 5 seconds
          if (isPageVisibleRef.current) {
            if (visibleHighlightTimerRef.current) {
              clearTimeout(visibleHighlightTimerRef.current);
            }
            visibleHighlightTimerRef.current = setTimeout(() => {
              setNewPronumbers(new Set());
              visibleHighlightTimerRef.current = null;
            }, 5000);
          }
        }
      } else {
        initialLoadDoneRef.current = true;
      }
      knownPronumbersRef.current = currentPros;

      // Merge any pending optimistic edits so polling doesn't overwrite
      // values the user just changed.
      const now = Date.now();
      for (const [pro, edit] of pendingEditsRef.current.entries()) {
        if (now - edit.timestamp > PENDING_EDIT_TTL_MS) {
          pendingEditsRef.current.delete(pro);
        }
      }

      if (pendingEditsRef.current.size > 0) {
        const merged = data.map((leg) => {
          const pending = pendingEditsRef.current.get(leg.pronumber);
          if (pending) {
            return { ...leg, ...pending.updates };
          }
          return leg;
        });
        setLegs(merged);
      } else {
        setLegs(data);
      }
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to fetch legs');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchRunningTotalsInternal = async (terminal?: string[]) => {
    try {
      setLoadingTotals(true);
      const data = await schedulingService.getRunningTotals(terminal);
      setRunningTotals(data);
    } catch (err: any) {
      console.error('Failed to fetch running totals:', err);
    } finally {
      setLoadingTotals(false);
    }
  };

  // Public fetch functions
  const fetchLegs = useCallback(async () => {
    await fetchLegsInternal(filters);
  }, [filters]);

  const fetchRunningTotals = useCallback(async () => {
    await fetchRunningTotalsInternal(filters.terminal);
  }, [filters.terminal]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      setLoadingOptions(true);
      const data = await schedulingService.getFilterOptions();
      setFilterOptions(data);
    } catch (err: any) {
      console.error('Failed to fetch filter options:', err);
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  // Set filters with debounced fetch
  const setFilters = useCallback((newFilters: SchedulerFilters) => {
    setFiltersState(newFilters);

    // Clear previous timeout
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
    }

    // Debounce the fetch
    filterTimeoutRef.current = setTimeout(() => {
      fetchLegsInternal(newFilters);
    }, 300);
  }, []);

  // Update a PRO's schedule (optimistic update)
  const updateLegSchedule = useCallback(
    async (pronumber: number, update: LegScheduleUpdate) => {
      // Resolve the note against current comments for optimistic display only.
      // The API prepends against its own current record, preserving newer comments.
      const optimisticUpdate = { ...update };
      if (update.scheduling_note?.trim()) {
        const current = legs.find(leg => leg.pronumber === pronumber)?.scheduling_comments;
        optimisticUpdate.scheduling_comments = update.scheduling_note.trim() + (current ? `\n\n${current}` : '');
      }
      delete optimisticUpdate.scheduling_note;
      // Record the edit so polling won't overwrite it for the next few seconds
      const existing = pendingEditsRef.current.get(pronumber);
      pendingEditsRef.current.set(pronumber, {
        updates: { ...(existing?.updates || {}), ...optimisticUpdate },
        timestamp: Date.now(),
      });

      // Update local state immediately (optimistic) - update all legs for this PRO
      // Also optimistically update status based on date changes (mirrors backend auto-management)
      setLegs((prev) =>
        prev.map((leg) => {
          if (leg.pronumber !== pronumber) return leg;

          // Merge the update
          const updatedLeg = { ...leg, ...optimisticUpdate };

          // HOLD clears the proposed prepull date server-side (unless already
          // finalized/sent to CMS). The payload can't carry it — prepull is
          // scheduler-only and the API would reject it — so mirror the clear here.
          if (update.schedule_status === 'HOLD' && !leg.prepull_finalized) {
            updatedLeg.proposed_prepull_date = null;
          }

          // Auto-manage status optimistically (only if status not explicitly set in update)
          if (!('schedule_status' in update)) {
            // Helper to check if date is valid (not null, empty, or epoch)
            const isValidDate = (d: string | null | undefined): boolean => {
              if (!d) return false;
              const parsed = new Date(d);
              return !isNaN(parsed.getTime()) && parsed.getFullYear() > 1970;
            };

            const hasDates = isValidDate(updatedLeg.proposed_appt_date) || isValidDate(updatedLeg.proposed_prepull_date);

            if (hasDates && !leg.schedule_status) {
              // Set to PLANNING when first date is penciled in
              updatedLeg.schedule_status = 'PLANNING';
            } else if (!hasDates && leg.schedule_status === 'PLANNING') {
              // Reset to null when all dates cleared (only if still in PLANNING)
              updatedLeg.schedule_status = null;
            }
          }

          return updatedLeg;
        })
      );

      try {
        await schedulingService.updateLegSchedule(pronumber, update);
        // Broadcast update to other users
        broadcastUpdate('leg_updated', { pronumber, update: optimisticUpdate });
      } catch (err: any) {
        // Revert on failure by refetching
        pendingEditsRef.current.delete(pronumber);
        console.error('Failed to update leg schedule:', err);
        fetchLegsInternal(filters, true);
        throw new Error(err.message || 'Failed to update leg');
      }
    },
    [broadcastUpdate, filters, legs]
  );

  // Send PROs to LC
  const sendToLC = useCallback(
    async (prosToSend: Array<{ pronumber: number }>) => {
      try {
        const result = await schedulingService.sendToLC({ legs: prosToSend });

        // Clear selection
        setSelectedLegs(new Set());

        // Refresh data (silent to preserve table filters)
        await fetchLegsInternal(filters, true);
        await fetchRunningTotalsInternal(filters.terminal);

        // Broadcast update
        broadcastUpdate('sent_to_lc', { pros: prosToSend, count: result.count });

        return result.count;
      } catch (err: any) {
        throw new Error(err.message || 'Failed to send to LC');
      }
    },
    [filters, broadcastUpdate]
  );

  // Finalize PROs (LC action)
  const finalizeLegs = useCallback(
    async (prosToFinalize: Array<{ pronumber: number }>) => {
      try {
        const recoveryPros = new Set(legs.filter(leg => leg.prepull_needed).map(leg => leg.pronumber));
        const recovery = prosToFinalize.filter(pro => recoveryPros.has(pro.pronumber));
        const ordinary = prosToFinalize.filter(pro => !recoveryPros.has(pro.pronumber));
        const result: { count: number; errors: string[] } = { count: 0, errors: [] };
        for (const group of [{ legs: recovery, prepull_only: true }, { legs: ordinary }]) {
          if (!group.legs.length) continue;
          const response = await schedulingService.finalizeLegs(group);
          result.count += response.count;
          result.errors.push(...(response.errors || []));
        }

        // Clear selection
        setSelectedLegs(new Set());

        // Refresh data (silent to preserve table filters)
        await fetchLegsInternal(filters, true);
        await fetchRunningTotalsInternal(filters.terminal);

        // Broadcast update
        broadcastUpdate('legs_finalized', { pros: prosToFinalize, count: result.count });

        return { count: result.count, errors: result.errors };
      } catch (err: any) {
        throw new Error(err.message || 'Failed to finalize');
      }
    },
    [filters, broadcastUpdate, legs]
  );

  // Finalize pre-pull only. Confirmed deliveries leave LC; other orders stay.
  const finalizePrepull = useCallback(
    async (prosToFinalize: Array<{ pronumber: number }>) => {
      try {
        // Scheduler already has access to /finalize. Explicit pre-pull-only mode
        // also prevents a concurrent delivery change from invoking delivery writes.
        const result = pageId === 'scheduler'
          ? await schedulingService.finalizeLegs({ legs: prosToFinalize, prepull_only: true })
          : await schedulingService.finalizePrepull({ legs: prosToFinalize });

        const refreshedLegs = await fetchLegsInternal(filters, true);
        if (refreshedLegs) {
          const visiblePros = new Set(refreshedLegs.map(leg => String(leg.pronumber)));
          setSelectedLegs(previous => new Set([...previous].filter(pro => visiblePros.has(pro))));
        }
        await fetchRunningTotalsInternal(filters.terminal);

        // Broadcast update
        broadcastUpdate('prepull_finalized', { pros: prosToFinalize, count: result.count });

        return { count: result.count, errors: result.errors };
      } catch (err: any) {
        throw new Error(err.message || 'Failed to finalize prepull');
      }
    },
    [filters, broadcastUpdate, pageId]
  );

  // Return PROs to scheduler (LC action)
  const returnToScheduler = useCallback(
    async (
      prosToReturn: Array<{ pronumber: number }>,
      targetDate?: string,
      targetOnOrBy?: 'ON' | 'BY'
    ) => {
      try {
        const result = await schedulingService.returnToScheduler({
          legs: prosToReturn,
          // Omitted entirely when the caller isn't required to give one —
          // sending an empty value would clear the order's target date. The
          // ON/BY qualifier travels with the date, never alone.
          ...(targetDate
            ? { target_delivery_date: targetDate, target_on_or_by: targetOnOrBy }
            : {}),
        });

        // Clear selection — but keep the failures selected so the LC can retry
        // them; those PROs are still sitting on the LC.
        if (!result.errors?.length) {
          setSelectedLegs(new Set());
        }

        // Refresh data (silent to preserve table filters)
        await fetchLegsInternal(filters, true);
        await fetchRunningTotalsInternal(filters.terminal);

        // Broadcast update
        broadcastUpdate('legs_returned', { pros: prosToReturn, count: result.count });

        return { count: result.count, errors: result.errors };
      } catch (err: any) {
        throw new Error(err.message || 'Failed to return to scheduler');
      }
    },
    [filters, broadcastUpdate]
  );

  // Selection management (by pronumber)
  const toggleLegSelection = useCallback((pronumber: number) => {
    const key = String(pronumber);
    setSelectedLegs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  const selectAllLegs = useCallback(() => {
    // Only select PROs with statuses that are selectable for bulk actions (null status is also selectable)
    const selectableLegs = legs.filter(
      (leg) => pageId === 'scheduler' ? isSchedulerLegSelectable(leg) : (leg.schedule_status === null || selectableStatuses.includes(leg.schedule_status))
    );
    // Use unique pronumbers
    const uniquePronumbers = [...new Set(selectableLegs.map((leg) => String(leg.pronumber)))];
    setSelectedLegs(new Set(uniquePronumbers));
  }, [legs, selectableStatuses, pageId]);

  // Select specific legs (for page-based selection)
  const selectLegs = useCallback((keys: string[]) => {
    setSelectedLegs(prev => {
      const newSet = new Set(prev);
      keys.forEach(key => newSet.add(key));
      return newSet;
    });
  }, []);

  // Deselect specific legs (for page-based deselection)
  const deselectLegs = useCallback((keys: string[]) => {
    setSelectedLegs(prev => {
      const newSet = new Set(prev);
      keys.forEach(key => newSet.delete(key));
      return newSet;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedLegs(new Set());
  }, []);

  // Refetch all data (silent to preserve table filters)
  const refetch = useCallback(async () => {
    await Promise.all([
      fetchLegsInternal(filters, true),
      fetchRunningTotalsInternal(filters.terminal),
    ]);
  }, [filters]);

  // Initial data fetch
  useEffect(() => {
    fetchLegsInternal(filters);
    fetchRunningTotalsInternal(filters.terminal);
    fetchFilterOptions();
  }, []); // Only run on mount

  // The added-column selection is part of the legs request, so a change to it has
  // to refetch. Keyed on the joined string rather than the array so a new array of
  // the same fields does not re-request. Skipped on mount — the effect above has
  // already fetched with whatever the selection was then.
  const didInitialLegsFetch = useRef(false);
  useEffect(() => {
    if (!didInitialLegsFetch.current) {
      didInitialLegsFetch.current = true;
      return;
    }
    fetchLegsInternal(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalFieldsKey]);

  // Track page visibility for new-pro detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      isPageVisibleRef.current = document.visibilityState === 'visible';
      // Cancel visible-highlight auto-clear when page goes hidden
      // (toast-based dismiss will take over on return)
      if (!isPageVisibleRef.current && visibleHighlightTimerRef.current) {
        clearTimeout(visibleHighlightTimerRef.current);
        visibleHighlightTimerRef.current = null;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // 15s polling for background refresh - pauses when tab is hidden
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  useVisibilityAwareInterval(() => {
    fetchLegsInternal(filtersRef.current, true);
  }, 15000);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
      if (visibleHighlightTimerRef.current) {
        clearTimeout(visibleHighlightTimerRef.current);
      }
    };
  }, []);

  return {
    // New pro detection
    newPronumbers,
    clearNewPronumbers,
    // State
    legs,
    runningTotals,
    filterOptions,
    selectedLegs,
    loading,
    loadingTotals,
    loadingOptions,
    error,
    filters,

    // Actions
    setFilters,
    fetchLegs,
    fetchRunningTotals,
    fetchFilterOptions,
    updateLegSchedule,
    sendToLC,
    finalizeLegs,
    finalizePrepull,
    returnToScheduler,
    toggleLegSelection,
    selectAllLegs,
    selectLegs,
    deselectLegs,
    clearSelection,
    refetch,

    // Real-time
    activeUsers,
    isConnected,
    socket,

    // Field editing collaboration
    fieldEditors,
    broadcastFieldFocus,
    broadcastFieldBlur,
    broadcastFieldValue,
    getFieldEditor,
  };
};
